import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Heart, Loader2, Smartphone, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import api, { formatApiError } from "@/lib/api";

export default function Login() {
  const { login, applyToken } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // OTP state
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);

  const go = (user) => {
    const dest = location.state?.from || (user.role === "admin" ? "/admin" : "/dashboard");
    navigate(dest);
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(t("login") + " ✓");
      go(user);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const sendOtp = async () => {
    setOtpLoading(true);
    try {
      await api.post("/auth/otp/request", { phone });
      setOtpSent(true);
      toast.success("OTP sent to " + phone);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setOtpLoading(false);
    }
  };

  const verifyOtp = async () => {
    setOtpLoading(true);
    try {
      const { data } = await api.post("/auth/otp/verify", { phone, code });
      const user = applyToken(data.token, data.user);
      toast.success(t("login") + " ✓");
      go(user);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setOtpLoading(false);
    }
  };

  return (
    <AuthShell title={t("login")}>
      <Tabs defaultValue="email" className="w-full">
        <TabsList className="grid grid-cols-2 w-full">
          <TabsTrigger value="email" data-testid="tab-email-login"><Mail className="h-4 w-4 mr-1.5" /> {t("email_login")}</TabsTrigger>
          <TabsTrigger value="otp" data-testid="tab-otp-login"><Smartphone className="h-4 w-4 mr-1.5" /> {t("otp_login")}</TabsTrigger>
        </TabsList>

        <TabsContent value="email">
          <form onSubmit={submit} className="space-y-4 mt-2" data-testid="login-form">
            <div>
              <Label htmlFor="email">{t("email")}</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="password">{t("password")}</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" className="mt-1.5" />
            </div>
            <div className="text-right">
              <Link to="/forgot-password" className="text-sm text-[#8B1E2B] hover:underline" data-testid="forgot-link">{t("forgot_password")}</Link>
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-[#8B1E2B] hover:bg-[#6f1622] text-white" data-testid="login-submit-button">
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {t("login")}
            </Button>
          </form>
        </TabsContent>

        <TabsContent value="otp">
          <div className="space-y-4 mt-2" data-testid="otp-login-form">
            <div>
              <Label htmlFor="phone">{t("mobile_number")}</Label>
              <Input id="phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+9198XXXXXXXX" disabled={otpSent} data-testid="otp-phone-input" className="mt-1.5" />
            </div>
            {!otpSent ? (
              <Button onClick={sendOtp} disabled={otpLoading || !phone} className="w-full bg-[#8B1E2B] hover:bg-[#6f1622] text-white" data-testid="otp-send-button">
                {otpLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {t("send_otp")}
              </Button>
            ) : (
              <>
                <div>
                  <Label htmlFor="code">{t("enter_otp")}</Label>
                  <Input id="code" inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" data-testid="otp-code-input" className="mt-1.5" />
                </div>
                <Button onClick={verifyOtp} disabled={otpLoading || !code} className="w-full bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="otp-verify-button">
                  {otpLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {t("verify_otp")}
                </Button>
                <button onClick={() => { setOtpSent(false); setCode(""); }} className="text-sm text-slate-500 hover:underline w-full text-center" data-testid="otp-change-number">Change number</button>
              </>
            )}
            <p className="text-xs text-slate-400">OTP sign-in requires the organiser to enable Twilio in Admin settings.</p>
          </div>
        </TabsContent>
      </Tabs>

      <p className="mt-6 text-center text-sm text-slate-600">
        {t("no_account")} <Link to="/register" className="text-[#8B1E2B] font-semibold hover:underline" data-testid="go-register">{t("register")}</Link>
      </p>
    </AuthShell>
  );
}

export function AuthShell({ title, children }) {
  const { t } = useLang();
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FAF6F0]">
      <div className="hidden lg:block relative">
        <div className="absolute inset-0 bg-[#0A2540]" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative h-full flex flex-col justify-center px-12 text-white">
          <Heart className="h-10 w-10 text-[#D4AF37]" fill="currentColor" />
          <h2 className="mt-4 font-serif text-4xl text-[#D4AF37]">101 Kanya Vivah</h2>
          <p className="mt-3 text-amber-50/85 max-w-sm">{t("club_name")} · {t("event_on")}, Jaipur</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 text-[#8B1E2B] font-serif text-xl" data-testid="auth-brand-link">
            <Heart className="h-5 w-5" fill="currentColor" /> 101 Kanya Vivah
          </Link>
          <h1 className="mt-6 font-serif text-3xl text-slate-900">{title}</h1>
          <div className="mt-6">{children}</div>
        </div>
      </div>
    </div>
  );
}
